import { cva } from 'class-variance-authority';
import { ChevronDown } from '@bridebook/aura/src/icons';
import { cn, SvgIcon, Icon } from '@bridebook/aura';
import {
  createContext,
  forwardRef,
  type ComponentPropsWithoutRef,
  useContext,
  ElementRef,
} from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Slot, Slottable } from '@radix-ui/react-slot';

type AccordionVariant = 'bordered' | 'default';

const AccordionVariantContext = createContext<AccordionVariant>('default');

const Accordion = forwardRef<
  ElementRef<typeof AccordionPrimitive.Root>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Root> & {
    /**
     * The variant of the accordion.
     */
    variant?: AccordionVariant;
  }
>(({ variant = 'default', className, ...props }, ref) => {
  const accordionVariants = cva('', {
    variants: {
      variant: {
        default: '',
        bordered: 'flex flex-col gap-4',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  });

  return (
    <AccordionVariantContext.Provider value={variant}>
      <AccordionPrimitive.Root
        ref={ref}
        className={cn(accordionVariants({ variant }), className)}
        {...props}
      />
    </AccordionVariantContext.Provider>
  );
});
Accordion.displayName = 'Accordion';

const AccordionItem = forwardRef<
  ElementRef<typeof AccordionPrimitive.Item>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => {
  const variant = useContext(AccordionVariantContext);

  const accordionVariants = cva('', {
    variants: {
      variant: {
        bordered: 'rounded-lg border border-[#DDE0E3]',
        default: 'border-b border-space-15',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  });

  return (
    <AccordionPrimitive.Item
      ref={ref}
      className={cn(accordionVariants({ variant }), className)}
      {...props}
    />
  );
});
AccordionItem.displayName = 'AccordionItem';

type AccordionTriggerProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
  icon?: SvgIcon;
  /**
   * When true, renders as a Slot instead of button, allowing custom wrapper elements.
   * Useful when you need interactive elements (like Checkbox) inside the trigger.
   */
  asChild?: boolean;
  children?: React.ReactNode;
};

const AccordionTrigger = forwardRef<
  ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ className, children, icon: IconComponent, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <AccordionPrimitive.Header className="flex" asChild>
      <div>
        <AccordionPrimitive.Trigger ref={ref} asChild {...props}>
          <Comp
            className={cn(
              'flex flex-1 items-center justify-between p-4 text-left text-base leading-6 font-semibold transition-all [&[data-state=open]>.chevron-icon]:rotate-180',
              className,
            )}>
            {IconComponent ? (
              <div className="flex items-center gap-2">
                <Icon icon={IconComponent} className="size-6 text-space" />
                <Slottable>{children}</Slottable>
              </div>
            ) : (
              <Slottable>{children}</Slottable>
            )}
            <Icon
              icon={ChevronDown}
              size={16}
              className="chevron-icon shrink-0 text-space transition-transform duration-200"
            />
          </Comp>
        </AccordionPrimitive.Trigger>
      </div>
    </AccordionPrimitive.Header>
  );
});
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

type AccordionContentProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
  classNames?: {
    wrapper?: string;
  };
};

const AccordionContent = forwardRef<
  ElementRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(({ className, classNames, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      'overflow-hidden px-4 text-base data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
      classNames?.wrapper,
    )}
    {...props}>
    <div className={cn('pt-0 pb-4', className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
